"use client"

import { useCallback, useEffect, useState } from "react"

import type { DeviceKey, InstallMethod } from "../types"

/** Versioned localStorage key so we can change the shape later without collisions. */
const STORAGE_KEY = "yoho.install.v1"
/** URL query parameter the user (or customer service) can use to deep-link. */
const QUERY_DEVICE = "device"
const QUERY_METHOD = "method"

/** What we persist to localStorage. Keep the shape small — read on every visit. */
type Persisted = {
  device?: DeviceKey
  method?: InstallMethod
}

export type InstallStateAPI = {
  device: DeviceKey
  method: InstallMethod
  setDevice: (next: DeviceKey) => void
  setMethod: (next: InstallMethod) => void
  /**
   * True once we've finished reconciling URL/storage/UA into the state.
   * Render guides should wait for this before showing "you're on iOS" copy
   * so we don't flash the wrong tab.
   */
  ready: boolean
}

const VALID_DEVICES: DeviceKey[] = [
  "ios",
  "android-samsung",
  "android-pixel",
  "android-xiaomi",
  "android-generic",
]
const VALID_METHODS: InstallMethod[] = ["qr", "manual", "deeplink"]

function isDeviceKey(value: unknown): value is DeviceKey {
  return typeof value === "string" && (VALID_DEVICES as string[]).includes(value)
}

function isInstallMethod(value: unknown): value is InstallMethod {
  return typeof value === "string" && (VALID_METHODS as string[]).includes(value)
}

function readPersisted(): Persisted {
  if (typeof window === "undefined") return {}
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Persisted
    return {
      device: isDeviceKey(parsed.device) ? parsed.device : undefined,
      method: isInstallMethod(parsed.method) ? parsed.method : undefined,
    }
  } catch {
    // localStorage throws under private-mode quotas; treat as empty.
    return {}
  }
}

function writePersisted(next: Persisted) {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Storage full / disabled — silently ignored. The URL still reflects state.
  }
}

function readUrl(): Persisted {
  if (typeof window === "undefined") return {}
  const params = new URLSearchParams(window.location.search)
  const device = params.get(QUERY_DEVICE)
  const method = params.get(QUERY_METHOD)
  return {
    device: isDeviceKey(device) ? device : undefined,
    method: isInstallMethod(method) ? method : undefined,
  }
}

function writeUrl(next: Persisted) {
  if (typeof window === "undefined") return
  const url = new URL(window.location.href)
  if (next.device) url.searchParams.set(QUERY_DEVICE, next.device)
  else url.searchParams.delete(QUERY_DEVICE)
  if (next.method) url.searchParams.set(QUERY_METHOD, next.method)
  else url.searchParams.delete(QUERY_METHOD)
  // history.replaceState — we don't want the back button to walk through
  // every tab/method toggle the user tried.
  window.history.replaceState(window.history.state, "", url.toString())
}

/**
 * Resolves the initial install selection from three sources, in priority
 * order:
 *
 *   1. URL query (deep links from CS / shared links)
 *   2. localStorage (returning user picks up where they left off)
 *   3. Caller-provided defaults (typically derived from UA via
 *      useDeviceDetect)
 *
 * Whenever the user clicks a different tab/method, we write back to both
 * the URL and localStorage so the three stay in sync.
 *
 * SSR-safe: during the first render we use the defaults; after mount we
 * reconcile and flip `ready` to true so render-time guards can take effect.
 */
export function useInstallState(defaults: {
  device: DeviceKey
  method: InstallMethod
}): InstallStateAPI {
  const [device, setDeviceState] = useState<DeviceKey>(defaults.device)
  const [method, setMethodState] = useState<InstallMethod>(defaults.method)
  const [ready, setReady] = useState(false)

  // Reconcile once on mount. We deliberately don't list defaults.* in the dep
  // array — defaults are the *initial* fallback only; further changes there
  // shouldn't clobber a user that has since clicked a tab.
  useEffect(() => {
    const url = readUrl()
    const persisted = readPersisted()
    const nextDevice = url.device ?? persisted.device ?? defaults.device
    const nextMethod = url.method ?? persisted.method ?? defaults.method
    setDeviceState(nextDevice)
    setMethodState(nextMethod)
    // Always echo the resolved state back to the URL so a copy-pasted link
    // remains accurate even when the user landed without parameters.
    writeUrl({ device: nextDevice, method: nextMethod })
    writePersisted({ device: nextDevice, method: nextMethod })
    setReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setDevice = useCallback((next: DeviceKey) => {
    setDeviceState(next)
    setMethodState((currentMethod) => {
      writeUrl({ device: next, method: currentMethod })
      writePersisted({ device: next, method: currentMethod })
      return currentMethod
    })
  }, [])

  const setMethod = useCallback((next: InstallMethod) => {
    setMethodState(next)
    setDeviceState((currentDevice) => {
      writeUrl({ device: currentDevice, method: next })
      writePersisted({ device: currentDevice, method: next })
      return currentDevice
    })
  }, [])

  return { device, method, setDevice, setMethod, ready }
}
